import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, FormField, Grid, List, Select, Text, TextArea, TextInput } from "grommet";
import { debounce } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../components/CustomButton";
import Flag from "../components/Flag";
import NavigateToPath from "../components/NavigateToPath";
import StyledModal from "../components/StyledModel";
import ThemeBuilder from "../components/ThemeBuilder";
import { usePath } from "../general/Helpers";
import { TeamRow } from "../teams/TeamsManagement";
import { getTeams, TeamViewModel } from "../teams/TeamsStore";
import MatchDetails from "./MatchDetails";
import { newMatchIcon } from "./MatchesIndex";
import { createMatch, CreateMatchRequest, MatchViewModel } from "./MatchesStore";
import styled from "styled-components";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;

const battleStyles = {
    container: {
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 50%, #3a3a3a 100%)',
        boxShadow: '0 0 30px rgba(var(--accent-rgb), 0.2), inset 0 0 20px rgba(var(--accent-rgb), 0.1)',
        border: '1px solid rgba(var(--accent-rgb), 0.3)',
        transition: 'all 0.3s ease',
        '@keyframes gradientBG': {
            '0%': {
                backgroundPosition: '0% 50%'
            },
            '50%': {
                backgroundPosition: '100% 50%'
            },
            '100%': {
                backgroundPosition: '0% 50%'
            }
        }
    },
    glowText: {
        background: 'linear-gradient(to right, #00ACED, #2B5CC4)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: '0 0 10px #00ACED, 0 0 20px #4338CA',
        animation: 'textGlow 2s ease-in-out infinite alternate'
    },
    card: {
        background: 'linear-gradient(45deg, rgba(var(--accent-rgb), 0.1), transparent)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
        border: '1px solid rgba(var(--accent-rgb), 0.1)',
        transition: 'all 0.3s ease',
        transform: 'translateY(0)',
    },
    cardHover: {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 25px rgba(var(--accent-rgb), 0.3)',
        border: '1px solid rgba(var(--accent-rgb), 0.3)'
    },
    button: {
        background: 'linear-gradient(45deg, var(--accent-1), var(--accent-2))',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 0 10px rgba(var(--accent-rgb), 0.2)',
        transition: 'all 0.3s ease',
        transform: 'scale(1)'
    },
    buttonHover: {
        transform: 'scale(1.05)',
        boxShadow: '0 0 20px rgba(var(--accent-rgb), 0.4), inset 0 0 10px rgba(var(--accent-rgb), 0.2)'
    },
    buttonActive: {
        transform: 'scale(0.98)'
    }
};

const CreateMatch = () => {
    let [organization, setOrganization] = React.useState("");

    let [match, setMatch] = React.useState(new MatchViewModel());

    let [isCreating, setIsCreating] = React.useState(false);

    let [error, setError] = React.useState("");

    const navigate = useNavigate();

    let create = () => {
        setIsCreating(true);
        createMatch({ ...match, participantIDs: participants.map((p: TeamViewModel) => p.id) }).then(
            (result: { matchID: string }) => {
                navigate(`/me/matches/${result.matchID}/`);
            },
            (error: any) => {
                setError(error);
            }
        );
    };

    const onChange = (updatedMatch: Partial<MatchViewModel>) => {
        setMatch({ ...match, ...updatedMatch });
    };
    const [teams, setTeams] = React.useState([] as TeamViewModel[]);

    React.useEffect(() => {
        getTeams(organization).then((teams: TeamViewModel[]) => {
            setTeams(teams);
            setTeamSearchResult(teams);
        });
    }, []);

    let [participants, setParticipants] = React.useState([] as TeamViewModel[]);

    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);

    const search = (filter: string) => {
        setTeamSearchResult(
            teams.filter((r: TeamViewModel) => {
                return r.name.toLowerCase().includes(filter.toLowerCase());
            })
        );
    };

    let [teamFilter, setTeamFilter] = React.useState("");
    const [role, setRole] = React.useState("");

    const [participantCreateError, setParticipantCreateError] = React.useState("");

    React.useEffect(() => {
        search(teamFilter);
    }, [teamFilter]);

    const addParticipant = (team: TeamViewModel) => {
        setParticipantCreateError("");
        if (!participants.find((p: TeamViewModel) => p.id === team.id)) {
            setParticipants([...participants, team]);
        }
    };

    const [showAddParticipantModal, setShowAddParticipantModal] = React.useState(false);
    const [showRemoveParticipantModal, setShowRemoveParticipantModal] = React.useState(false);
    const [participantToRemove, setParticipantToRemove] = React.useState({} as TeamViewModel);

    const startRemoveParticipant = (participant: TeamViewModel) => {
        setParticipantToRemove(participant);
        setShowRemoveParticipantModal(true);
    };

    const removeParticipant = (participant: TeamViewModel) => {
        setParticipants(participants.filter((p: TeamViewModel) => p.id !== participant.id));
        setShowRemoveParticipantModal(false);
    };

    const { pathname } = useLocation();

    return (
        <Box fill>
            <Box 
                background="linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 50%, #3a3a3a 100%)" 
                pad="medium" 
                round="small"
                margin={{ bottom: "medium" }}
                animation={["fadeIn", "slideDown"]}
                style={{
                    boxShadow: '0 0 30px rgba(var(--accent-rgb), 0.2), inset 0 0 20px rgba(var(--accent-rgb), 0.1)',
                    border: '1px solid rgba(var(--accent-rgb), 0.3)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box 
                    direction="row" 
                    justify="between" 
                    align="center"
                >
                    <Box>
                        <Text
                            size="xlarge"
                            weight="bold"
                            style={{ 
                                ...battleStyles.glowText,
                                textTransform: "uppercase", 
                                letterSpacing: "3px",
                                fontSize: "2.2em",
                                fontWeight: "800"
                            }}
                        >
                            Create New Match
                        </Text>
                        <Text size="small" color="accent-2">Configure Your Epic Matchup</Text>
                    </Box>
                    <NavigateToPath path="/matches/create"></NavigateToPath>
                </Box>
            </Box>
            <Box
                width={{ max: "800px" }}
                pad="medium"
                round="medium"
                gap="medium"
                border={{ color: 'accent-1', size: '2px' }}
                style={{
                    ...battleStyles.container,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box
                    background="linear-gradient(to bottom, #222222, #1a1a1a)"
                    pad="medium"
                    round="medium"
                    gap="medium"
                    border={{ color: 'accent-2', size: '1px' }}
                    animation={{ type: "fadeIn", duration: 500 }}
                >
                    <MatchDetails match={match} isBusy={isCreating} onChange={onChange} />
                </Box>
                <Box 
                    background="linear-gradient(to bottom, #222222, #1a1a1a)"
                    pad="medium"
                    round="medium"
                    gap="medium"
                    border={{ color: 'accent-2', size: '1px' }}
                >
                    <Box
                        direction="row"
                        align="center"
                        gap="small"
                        pad={{ bottom: "small" }}
                        border={{ side: 'bottom', color: 'accent-2', size: '1px' }}
                    >
                        <FontAwesomeIcon icon={["fad", "users-class"]} size="lg" color="var(--accent-2)" />
                        <Text 
                            weight="bold"
                            size="large"
                            color="accent-2"
                            style={{ 
                                textTransform: "uppercase",
                                letterSpacing: "1px"
                            }}
                        >
                            Match Participants
                        </Text>
                    </Box>
                    <Box pad={"small"} animation={{ type: "fadeIn", duration: 500 }}>
                        {participants.length == 0 && (
                            <Text color="accent-2" style={{ fontStyle: 'italic' }}>
                                No teams have joined the match yet
                            </Text>
                        )}
                        {participants.map((participant: TeamViewModel, index: number) => (
                            <Box
                                key={index}
                                animation={{ type: "slideRight", delay: index * 100 }}
                                margin={{ vertical: "xsmall" }}
                            >
                                <TeamRow size="small" team={participant}>
                                    <CustomButton 
                                        primary 
                                        label="Remove" 
                                        size="small" 
                                        icon={xmark} 
                                        onClick={() => startRemoveParticipant(participant)}
                                        color="status-critical"
                                        style={{
                                            transform: "scale(1)",
                                            transition: "transform 0.2s ease",
                                            ":hover": {
                                                transform: "scale(1.05)"
                                            }
                                        }}
                                    />
                                </TeamRow>
                            </Box>
                        ))}
                    </Box>
                    <CustomButton 
                        primary 
                        icon={plus} 
                        label="Add Team" 
                        onClick={() => setShowAddParticipantModal(true)}
                        color="accent-2"
                        style={{
                            transform: "scale(1)",
                            transition: "all 0.3s ease",
                            background: 'linear-gradient(45deg, var(--accent-1), var(--accent-2))',
                            border: '1px solid rgba(255,255,255,0.1)',
                            boxShadow: '0 0 10px rgba(var(--accent-rgb), 0.2)',
                            ":hover": {
                                transform: "scale(1.05)",
                                boxShadow: '0 0 20px rgba(var(--accent-rgb), 0.4), inset 0 0 10px rgba(var(--accent-rgb), 0.2)'
                            },
                            ":active": {
                                transform: "scale(0.98)"
                            }
                        }}
                    />
                </Box>

                <StyledModal isOpen={showAddParticipantModal} setIsOpen={setShowAddParticipantModal}>
                    <Box
                        width={"medium"}
                        background="linear-gradient(to bottom, #2a2a2a, #1a1a1a)"
                        pad="medium"
                        round="medium"
                        gap="medium"
                        border={{ color: 'accent-1', size: '2px' }}
                        style={{
                            boxShadow: '0 0 20px rgba(0,0,0,0.3)'
                        }}
                    >
                        <Box
                            direction="row"
                            align="center"
                            gap="small"
                            pad={{ bottom: "small" }}
                            border={{ side: 'bottom', color: 'accent-1', size: '1px' }}
                        >
                            <FontAwesomeIcon icon={["fad", "user-plus"]} size="lg" color="var(--accent)" />
                            <Text 
                                weight="bold"
                                size="large"
                                color="accent-1"
                                style={{ 
                                    textTransform: "uppercase",
                                    letterSpacing: "1px"
                                }}
                            >
                                Add Teams
                            </Text>
                        </Box>
                        
                        <FormField label={
                            <Text color="accent-2">Search Teams</Text>
                        }>
                            <TextInput 
                                size="medium"
                                value={teamFilter}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTeamFilter(e.target.value)}
                                style={{
                                    background: 'rgba(0,0,0,0.2)',
                                    border: '1px solid var(--accent-2)'
                                }}
                            />
                        </FormField>
                        
                        <Box gap="small" animation={{ type: "fadeIn", duration: 500 }}>
                            {teamSearchResult.length == 0 && (
                                <Text color="accent-2" style={{ fontStyle: 'italic' }}>
                                    No teams found in the match
                                </Text>
                            )}
                            {teamSearchResult.map((team: TeamViewModel) => (
                                <Box
                                    key={team.id}
                                    animation={{ type: "slideRight" }}
                                >
                                    <TeamRow team={team}>
                                        <CustomButton 
                                            primary 
                                            icon={plus}
                                            label="Select"
                                            onClick={() => addParticipant(team)}
                                            color="accent-1"
                                            style={{
                                                transform: "scale(1)",
                                                transition: "transform 0.2s ease",
                                                ":hover": {
                                                    transform: "scale(1.05)"
                                                }
                                            }}
                                        />
                                    </TeamRow>
                                </Box>
                            ))}
                        </Box>
                        
                        {participantCreateError && (
                            <Text color="status-critical">
                                {participantCreateError}
                            </Text>
                        )}
                        
                        <Box direction="row" justify="end" gap="small">
                            <CustomButton 
                                primary
                                label="Close Portal"
                                onClick={() => setShowAddParticipantModal(false)}
                                color="accent-2"
                                style={{
                                    transform: "scale(1)",
                                    transition: "transform 0.2s ease",
                                    ":hover": {
                                        transform: "scale(1.05)"
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </StyledModal>

                <StyledModal isOpen={showRemoveParticipantModal} setIsOpen={setShowRemoveParticipantModal}>
                    <Box
                        width={"medium"}
                        background="linear-gradient(to bottom, #2a2a2a, #1a1a1a)"
                        pad="medium"
                        round="medium"
                        gap="medium"
                        border={{ color: 'accent-4', size: '2px' }}
                        style={{
                            boxShadow: '0 0 20px var(--accent-4)'
                        }}
                    >
                        <Box
                            direction="row"
                            align="center"
                            gap="small"
                            pad={{ bottom: "small" }}
                            border={{ side: 'bottom', color: 'status-critical', size: '1px' }}
                        >
                            <FontAwesomeIcon icon={["fad", "exclamation-triangle"]} size="lg" color="var(--status-critical)" />
                            <Text 
                                weight="bold"
                                color="status-critical"
                            >
                                Confirm Team Removal
                            </Text>
                        </Box>
                        
                        <Text color="accent-2">
                            Are you sure you want to remove this Team from the match?
                        </Text>
                        
                        <Box direction="row" justify="end" gap="small">
                            <CustomButton 
                                onClick={() => setShowRemoveParticipantModal(false)} 
                                label="Cancel"
                                style={{
                                    transform: "scale(1)",
                                    transition: "transform 0.2s ease",
                                    ":hover": {
                                        transform: "scale(1.05)"
                                    }
                                }}
                            />
                            <CustomButton 
                                primary
                                onClick={() => removeParticipant(participantToRemove)} 
                                label="Remove" 
                                color="status-critical"
                                style={{
                                    transform: "scale(1)",
                                    transition: "transform 0.2s ease",
                                    ":hover": {
                                        transform: "scale(1.05)"
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </StyledModal>

                <Box
                    direction="row"
                    justify="end"
                    margin={{ top: "medium" }}
                >
                    <CustomButton 
                        primary 
                        icon={newMatchIcon} 
                        onClick={create} 
                        disabled={isCreating || participants.length == 0} 
                        label={isCreating ? "Forging match..." : "Forge match"}
                        color="accent-1"
                        style={{
                            ...battleStyles.button,
                            '&:hover': battleStyles.buttonHover,
                            '&:active': battleStyles.buttonActive
                        }}
                    />
                </Box>
                
                {error && (
                    <Box
                        background="#ff000022"
                        pad="medium"
                        round="small"
                        border={{ color: 'status-critical', size: '1px' }}
                        margin={{ top: "medium" }}
                    >
                        <Text color="status-critical">
                            {typeof error === 'string' ? error : JSON.stringify(error, null, 2)}
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CreateMatch;
